/**
 * CRM Patients API
 * Patient profile operations in the CRM context
 */

import { crmGet, crmPost, crmPut, crmDelete } from './crmConfig';

/**
 * Fetch all patients with pagination and filters.
 *
 * Routing:
 *  - When `search` is provided, hit `/search-patient` which searches the entire
 *    collection by Full Name / Email / phone (regex, case-insensitive).
 *  - Otherwise hit `/all-patients` which supports `statusFilter` and `tagFilter`.
 *
 * The `/search-patient` endpoint ignores status/tag filters server-side; those
 * are applied client-side by the caller when search is active.
 *
 * @param {Object} params - { page, limit, search, statusFilter, tagFilter }
 */
export const fetchCRMPatients = async (params) => {
  const { search, page = 1, limit = 25, statusFilter, tagFilter } = params || {};
  const hasSearch = typeof search === 'string' && search.trim().length > 0;

  if (hasSearch) {
    const response = await crmGet('/api/patientProfiles/search-patient', {
      query: search.trim(),
      page,
      limit,
    });
    const data = response.data || {};
    const items = Array.isArray(data.patients) ? data.patients : [];
    const totalPages = data.totalPages || 1;
    // /search-patient doesn't return a total count, derive from totalPages * limit (upper bound).
    return {
      items,
      totalCount: typeof data.totalCount === 'number' ? data.totalCount : items.length + (totalPages > 1 ? (totalPages - 1) * limit : 0),
      totalPages,
      currentPage: data.currentPage || page,
    };
  }

  const response = await crmGet('/api/patientProfiles/all-patients', {
    page,
    limit,
    ...(statusFilter ? { statusFilter } : {}),
    ...(tagFilter ? { tagFilter } : {}),
  });
  const data = response.data || {};
  return {
    items: Array.isArray(data.data) ? data.data : (Array.isArray(data.patients) ? data.patients : []),
    totalCount: data.totalProfiles || data.totalCount || 0,
    totalPages: data.totalPages || 1,
    currentPage: data.page || page,
  };
};

/**
 * Fetch single patient profile by ID
 * @param {string} id - Patient ID
 */
export const fetchCRMPatientById = async (id) => {
  const response = await crmGet(`/api/patientProfiles/${id}`);
  return response.data;
};

/**
 * Fetch patient count (no filters)
 */
export const fetchPatientCount = async () => {
  const response = await crmGet('/api/patientProfiles');
  return response.data;
};

/**
 * Create patient profile from lead
 * @param {string} leadId - Lead ID to convert to patient
 */
export const convertLeadToPatient = async (leadId) => {
  const response = await crmPost(`/api/patientProfiles/create/${leadId}`);
  return response.data;
};

/**
 * Partial update of a patient profile (PUT supports $set via findByIdAndUpdate).
 * Accepts any subset of fields: notes, tags, Status, messages, attachments, etc.
 *
 * Note: named `updateCRMPatientProfile` to avoid colliding with the legacy
 * `updatePatientProfile` exported by `patientsApi.js`. Both files are
 * re-exported via `export *` from `api/index.js`, so identical names would
 * make webpack throw a "conflicting star exports" error.
 */
export const updateCRMPatientProfile = async (id, changes) => {
  const response = await crmPut(`/api/patientProfiles/${id}`, changes);
  return response.data;
};

/**
 * Delete a patient profile (CRM scope — see note on updateCRMPatientProfile).
 */
export const deleteCRMPatientProfile = async (id) => {
  const response = await crmDelete(`/api/patientProfiles/${id}`);
  return response.data;
};
